import * as React from "react"
import { Fragment, useEffect } from "react"
import { batch, useSignal, useComputed } from "@preact/signals-react";
import { useSearchParams } from "react-router-dom";
import { Grid, Skeleton, Fade } from "@mui/material";
import { ButtonSeeker } from "@ui-seeker/core";
import { decodingJSON, isEmpty, mergeDicts } from "@ui-seeker/utils";

import {
    haveEncodedValue,
    isLoadingCommonFilters,
    dynamicCommonFiltersValue,
    resetCommonFilters,
    setCommonFilters,
    submittedFilters,
    startDate,
    endDate,
    favFilterSelected,
    disabledCommonFiltersSubmit,
    loadedFavFilters,
    isSystemBusy
} from "@signals";
import { COMPONENTS } from "./@constants";
import { useCommonFiltersDefault } from "./@hooks";
import CommonFiltersFooter from "./Footer";


export default function DynamicCommonFilters({ uniqueID, blockID, components = [], downloadCSV=null, downloadCSVOptions=null, stayDefaultFilters=false }) {
    const [searchParams] = useSearchParams()
    const { isLoadingDefaultFilters, defaultFilters } = useCommonFiltersDefault(uniqueID, blockID)
    const launchedDefaultFilters = useSignal(false)
    const spEncode = searchParams.get('sp')

    const stayDefaultFiltersSignal = useSignal(stayDefaultFilters)
    const showMoreFilters = useSignal(false)
    const buttonFiltersText = useComputed(() => {
        return !showMoreFilters.value ? 'Show More Filters' : 'Hide More Filters'
    })
    const openFilters = useSignal({})

    useEffect(() => {
        return () => batch(() => {
            submittedFilters.value = {}
            isLoadingCommonFilters.value = false
            isSystemBusy.value = false
            resetCommonFilters()
            disabledCommonFiltersSubmit.value = false
            loadedFavFilters.value = false
        })
    }, []);

    useEffect(() => {
        if (!isLoadingDefaultFilters && loadedFavFilters.value) {
            if (spEncode) {
                setCommonFilters(mergeDicts(defaultFilters, decodingJSON(spEncode)))
                haveEncodedValue.value = true
            } else {
                setCommonFilters(mergeDicts(defaultFilters, favFilterSelected.value))
            }
            isLoadingCommonFilters.value = true
            if (stayDefaultFiltersSignal.value)
                stayDefaultFiltersSignal.value = false
        }
    }, [isLoadingDefaultFilters, loadedFavFilters.value]);

    useEffect(() => {
        const launch = searchParams.get('launch')

        if (spEncode && launch && launch.toLowerCase() === 'true' && !isLoadingDefaultFilters && !isLoadingCommonFilters.value && !launchedDefaultFilters.value && haveEncodedValue.value) {
            isLoadingCommonFilters.value = true
            launchedDefaultFilters.value = true
        } else if (!spEncode && launch && launch.toLowerCase() === 'true' && !isLoadingDefaultFilters && !isLoadingCommonFilters.value && !launchedDefaultFilters.value) {
            isLoadingCommonFilters.value = true
            launchedDefaultFilters.value = true
        }
    }, [isLoadingDefaultFilters])

    useEffect(() => {
        if (isLoadingCommonFilters.value) {
            submittedFilters.value = { ...dynamicCommonFiltersValue.value }
        }

        if (showMoreFilters.value) {
            openFilters.value = dynamicCommonFiltersValue.peek()
            disabledCommonFiltersSubmit.value = true
        }
    }, [isLoadingCommonFilters.value])

    useEffect(() => {
        if (!showMoreFilters.value && !stayDefaultFiltersSignal.value)
            isLoadingCommonFilters.value = true
    }, [startDate.value, endDate.value, favFilterSelected.value])

    useEffect(() => {
        if (showMoreFilters.value) {
            openFilters.value = dynamicCommonFiltersValue.peek()
        }

        if (JSON.stringify(openFilters.value) === JSON.stringify(dynamicCommonFiltersValue.value)) {
            disabledCommonFiltersSubmit.value = true
        } else {
            disabledCommonFiltersSubmit.value = false
        }

    }, [showMoreFilters.value])

    useEffect(() => {
        if (showMoreFilters.value) {
            if (JSON.stringify(openFilters.value) === JSON.stringify(dynamicCommonFiltersValue.value)) {
                disabledCommonFiltersSubmit.value = true
            } else {
                disabledCommonFiltersSubmit.value = false
            }
        }
    }, [dynamicCommonFiltersValue.value])

    if (isLoadingDefaultFilters)
        return <Skeleton variant="rectangular" height={500} />

    return <>
        <Grid container spacing={2}  sx={{ mt: 1 }}>
            {components.map(({ name, hide=true, grid = {}, sx = {}, ...props }, idx) => {
            
                if (name === 'blank')
                    return <Grid key={idx} item  {...grid} sx={sx} />
                
                if (name === 'showMoreFilters')
                    return <Grid key={idx} item {...grid} sx={sx} >
                        <ButtonSeeker onClick={() => showMoreFilters.value = !showMoreFilters.value} >
                            {buttonFiltersText.value}
                        </ButtonSeeker>
                    </Grid>

                const Component = COMPONENTS[name]
                if (!Component)
                    return <Fragment key={name} />
                
                if (!hide)
                    return <Grid key={name} item xs={4} {...grid} sx={sx}>
                        <Component {...props} />
                    </Grid>
                
            })}

        </Grid>

        <Fade 
            in={showMoreFilters.value} 
            unmountOnExit={false}
            timeout={50}
            sx={{ maxHeight: !showMoreFilters.value ? 0 : 2000, mt: 1 }}
        >
            <Grid container spacing={2}  sx={{ mt: 1 }}>
                {components.map(({ name, hide=true, grid = {}, sx = {}, ...props }, idx) => {
                
                
                    const Component = COMPONENTS[name]
                    if (!Component)
                        return <Fragment key={name} />
                    
                    if (hide)
                        return <Grid key={name} item xs={4} {...grid} sx={sx}>
                            <Component {...props} />
                        </Grid>
                    
                })}

                {showMoreFilters.value && <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start'}}>
                    <CommonFiltersFooter downloadCSV={downloadCSV} downloadCSVOptions={downloadCSVOptions} />
                </Grid>}
            </Grid>
        </Fade>
    </>
}